
import React, { useMemo, useState } from "react";
import "./PalaceAnalytics.css";

const periods = ["7 Days", "30 Days", "90 Days", "12 Months"];

const demoData = {
  "7 Days": {
    revenue: 48500,
    bookings: 18,
    nights: 42,
    occupancy: 64,
    previousRevenue: 41200,
    previousBookings: 15,
    chart: [35, 52, 43, 75, 60, 88, 68],
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  },
  "30 Days": {
    revenue: 186400,
    bookings: 74,
    nights: 192,
    occupancy: 71,
    previousRevenue: 162000,
    previousBookings: 61,
    chart: [35, 48, 42, 63, 55, 75, 67, 90, 72, 82, 61, 78],
    labels: ["1", "4", "7", "10", "13", "16", "19", "22", "25", "28", "30", ""],
  },
  "90 Days": {
    revenue: 524800,
    bookings: 216,
    nights: 568,
    occupancy: 68,
    previousRevenue: 468000,
    previousBookings: 190,
    chart: [32, 44, 39, 57, 48, 65, 59, 75, 68, 86, 73, 94],
    labels: ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12"],
  },
  "12 Months": {
    revenue: 2186400,
    bookings: 912,
    nights: 2450,
    occupancy: 73,
    previousRevenue: 1940000,
    previousBookings: 820,
    chart: [42, 48, 54, 46, 62, 70, 58, 77, 68, 85, 80, 96],
    labels: ["Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"],
  },
};

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const growth = (current, previous) =>
  previous ? Math.round(((current - previous) / previous) * 100) : 0;

export default function PalaceAnalytics() {
  const [period, setPeriod] = useState("30 Days");
  const [property, setProperty] = useState("All Palaces");
  const data = demoData[period];

  const metrics = useMemo(
    () => [
      {
        label: "Total Revenue",
        value: money(data.revenue),
        change: growth(data.revenue, data.previousRevenue),
        icon: "₹",
        color: "purple",
        detail: "Revenue in selected period",
      },
      {
        label: "Total Bookings",
        value: data.bookings.toLocaleString("en-IN"),
        change: growth(data.bookings, data.previousBookings),
        icon: "▣",
        color: "blue",
        detail: "Reservations received",
      },
      {
        label: "Room Nights",
        value: data.nights.toLocaleString("en-IN"),
        change: 12,
        icon: "▤",
        color: "orange",
        detail: "Nights booked",
      },
      {
        label: "Occupancy Rate",
        value: `${data.occupancy}%`,
        change: 5,
        icon: "◔",
        color: "green",
        detail: "Average room occupancy",
      },
    ],
    [data]
  );

  const sources = [
    { name: "Website", percent: 48, amount: 48 },
    { name: "Mobile App", percent: 32, amount: 32 },
    { name: "Other Channels", percent: 20, amount: 20 },
  ];

  const bookingStatuses = [
    { name: "Confirmed", count: 42, color: "confirmed" },
    { name: "Pending", count: 12, color: "pending" },
    { name: "Cancelled", count: 8, color: "cancelled" },
    { name: "Completed", count: 12, color: "completed" },
  ];

  return (
    <div className="pa-analytics">
      <div className="pa-header">
        <div>
          <div className="pa-eyebrow">PALACE PERFORMANCE</div>
          <h1>Analytics Overview</h1>
          <p>Track revenue, reservations, room occupancy and business performance.</p>
        </div>

        <div className="pa-header-actions">
          <select value={property} onChange={(e) => setProperty(e.target.value)}>
            <option>All Palaces</option>
          </select>
          <button
            className="pa-export"
            onClick={() => window.print()}
            type="button"
          >
            ↓ Export / Print
          </button>
        </div>
      </div>

      <div className="pa-demo-notice">
        <span>i</span>
        Demo analytics data — connect your booking and payment APIs for actual figures.
      </div>

      <div className="pa-periods">
        {periods.map((item) => (
          <button
            type="button"
            key={item}
            className={period === item ? "active" : ""}
            onClick={() => setPeriod(item)}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="pa-metrics">
        {metrics.map((metric) => (
          <div className="pa-metric-card" key={metric.label}>
            <div className="pa-metric-top">
              <span>{metric.label}</span>
              <span className={`pa-metric-icon ${metric.color}`}>
                {metric.icon}
              </span>
            </div>
            <h2>{metric.value}</h2>
            <div className="pa-metric-bottom">
              <span className={metric.change >= 0 ? "pa-positive" : "pa-negative"}>
                {metric.change >= 0 ? "↗" : "↘"} {Math.abs(metric.change)}%
              </span>
              <span>{metric.detail}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="pa-main-grid">
        <section className="pa-card pa-revenue-card">
          <div className="pa-card-heading">
            <div>
              <h3>Revenue Performance</h3>
              <p>Revenue trend across the selected period</p>
            </div>
            <span className="pa-live-label">● Demo</span>
          </div>

          <div className="pa-chart-summary">
            <strong>{money(data.revenue)}</strong>
            <span>Revenue</span>
          </div>

          <div className="pa-chart">
            <div className="pa-grid-lines">
              <span>{money(data.revenue)}</span>
              <span>{money(data.revenue * 0.75)}</span>
              <span>{money(data.revenue * 0.5)}</span>
              <span>{money(data.revenue * 0.25)}</span>
            </div>

            <div className="pa-bars">
              {data.chart.map((value, index) => (
                <div className="pa-bar-column" key={`${period}-${index}`}>
                  <div className="pa-bar-track">
                    <div
                      className="pa-bar"
                      style={{ height: `${value}%` }}
                      title={`${value}% relative performance`}
                    />
                  </div>
                  <span>{data.labels[index]}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pa-card">
          <div className="pa-card-heading">
            <div>
              <h3>Booking Status</h3>
              <p>Reservation breakdown</p>
            </div>
            <span className="pa-small-badge">{data.bookings} bookings</span>
          </div>

          <div className="pa-status-list">
            {bookingStatuses.map((status) => (
              <div className="pa-status-row" key={status.name}>
                <span className={`pa-status-dot ${status.color}`} />
                <span className="pa-status-name">{status.name}</span>
                <strong>{status.count}</strong>
                <div className="pa-status-track">
                  <span
                    className={status.color}
                    style={{
                      width: `${
                        (status.count /
                          bookingStatuses.reduce((sum, item) => sum + item.count, 0)) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="pa-status-footer">
            <span>Total reservations</span>
            <strong>
              {bookingStatuses.reduce((sum, item) => sum + item.count, 0)}
            </strong>
          </div>
        </section>
      </div>

      <div className="pa-bottom-grid">
        <section className="pa-card">
          <div className="pa-card-heading">
            <div>
              <h3>Booking Channels</h3>
              <p>Illustrative reservation source split</p>
            </div>
          </div>

          <div className="pa-channel-list">
            {sources.map((source) => (
              <div className="pa-channel-item" key={source.name}>
                <div className="pa-channel-top">
                  <span>{source.name}</span>
                  <strong>{source.percent}%</strong>
                </div>
                <div className="pa-channel-track">
                  <span style={{ width: `${source.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="pa-card">
          <div className="pa-card-heading">
            <div>
              <h3>Room Performance</h3>
              <p>Selected period summary</p>
            </div>
          </div>

          <div className="pa-room-summary">
            <div>
              <span className="pa-room-icon">▤</span>
              <div>
                <strong>{data.nights}</strong>
                <span>Room nights booked</span>
              </div>
            </div>
            <div>
              <span className="pa-room-icon green">◔</span>
              <div>
                <strong>{data.occupancy}%</strong>
                <span>Occupancy rate</span>
              </div>
            </div>
            <div>
              <span className="pa-room-icon orange">₹</span>
              <div>
                <strong>
                  {money(data.nights ? Math.round(data.revenue / data.nights) : 0)}
                </strong>
                <span>Revenue per room night</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="pa-footer-note">
        <span>ⓘ</span>
        Figures on this page are illustrative and are not connected to live bookings yet.
      </div>
    </div>
  );
}
